// Admin "view as" (2026-09-25): which teacher or student this browser tab is
// viewing as. An admin opens it from a teacher's or student's admin page; the
// real teacher / student dashboard then renders that person's data, read only.
//
// Kept in sessionStorage: this tab only, and a reload stays in view mode. A new
// tab (even one the page opens) starts outside view mode. Opening any admin
// page ends view mode (ViewAsProvider).
import { useSyncExternalStore } from 'react';

const KEY = 'bb.viewAs.v1';
const listeners = new Set();

// The admin API's account-id rule (view-as.mjs ID_RE): a Cognito sub.
export const isViewAsId = (id) => typeof id === 'string' && /^[A-Za-z0-9-]{8,64}$/.test(id);

// Who is doing the viewing (2026-10-27): an admin (any teacher or student at
// their school) or a teacher (a student in one of their classes, named by
// classId so the admin API can check it). Views saved before this are an admin's.
export const VIEWERS = {
  Admin: { home: '/admin-dashboard', back: 'Back to admin' },
  Teacher: { home: '/teacher-dashboard', back: 'Back to my class' },
};
export const viewerOf = (v) => v?.viewer || 'Admin';
export const backLabelOf = (v) => VIEWERS[viewerOf(v)]?.back || VIEWERS.Admin.back;
const valid = (v) => !!v && (v.kind === 'teacher' || v.kind === 'student') && isViewAsId(v.id)
  && (viewerOf(v) === 'Admin'
    || (viewerOf(v) === 'Teacher' && v.kind === 'student' && typeof v.classId === 'string' && v.classId.length > 0 && v.classId.length <= 128));

function read() {
  try {
    const v = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    return valid(v) ? v : null;
  } catch { return null; }
}

let state = read();

function set(next) {
  state = next;
  try {
    if (next) sessionStorage.setItem(KEY, JSON.stringify(next));
    else sessionStorage.removeItem(KEY);
  } catch { /* storage off: this page load only */ }
  listeners.forEach((fn) => { try { fn(state); } catch { /* isolated */ } });
}

export const getViewAs = () => state;
export function subscribeViewAs(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function useViewAs() {
  return useSyncExternalStore(subscribeViewAs, getViewAs, getViewAs);
}

// target: { kind: 'teacher'|'student', id, name, email?, school?, grade?, returnTo?, viewer?, classId? }
export function beginViewAs(target) {
  const viewer = target.viewer || 'Admin';
  const next = {
    kind: target.kind,
    id: target.id,
    name: target.name || (target.kind === 'teacher' ? 'Teacher' : 'Student'),
    email: target.email || null,
    school: target.school || null,
    grade: target.grade ?? null,
    returnTo: target.returnTo || VIEWERS[viewer]?.home || '/admin-dashboard',
    startedAt: Date.now(),
    // A teacher's view only: an admin's keeps its original shape.
    ...(viewer === 'Admin' ? {} : { viewer, classId: target.classId || null }),
  };
  if (!valid(next)) throw new Error('invalid view-as target');
  set(next);
}
export function endViewAs() { if (state) set(null); }

// The pages each view can open. Anything else shows "not part of view mode".
export const VIEW_ROUTES = {
  teacher: ['/teacher-dashboard'],
  student: ['/student-dashboard', '/student-homework', '/session', '/placement', '/colleges'],
};
const under = (pathname, p) => pathname === p || pathname.startsWith(`${p}/`);
export const routeAllowed = (kind, pathname) => (VIEW_ROUTES[kind] || []).some((p) => under(pathname, p));
export const isAdminRoute = (pathname) => under(pathname, '/admin-dashboard') || pathname.startsWith('/admin-dashboard-');
// The viewer's own pages: opening one ends view mode, and they are never guarded.
export const isViewerHome = (viewer, pathname) => (viewer === 'Teacher'
  ? under(pathname, '/teacher-dashboard') || pathname.startsWith('/teacher-dashboard-')
  : isAdminRoute(pathname));
export const homeOf = (kind) => (kind === 'teacher' ? '/teacher-dashboard' : '/student-dashboard');
